import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { IDataItem } from "./types";
import { getAllKeys } from "../../../functions";
import { connect } from "react-redux";
import { StoreState } from "../../../state/configureStore";
import { DimensionsReducer } from "../../../state/reducers/app";
import { DateTime } from "luxon";
import { holidays } from "../../../utils/hollidays";

export const colors: Array<string> = [
  "#01204E", 
  "#028391", 
  "#F6DCAC",
  "#FEAE6F", 
  "#f9b4ab", 
  "#264e70", 
  "#679186", 
  "#bbd4ce", 
  "#0A3A70", 
  "#01606A", 
  "#03A9B4",
  "#F8E5C1", 
  "#FF9843", 
  "#F29C93", 
  "#38668C", 
  "#1B374F", 
  "#4C7368", 
  "#A3C4BC", 
  "#5996c4",
  "#2c573f"
];

export type IColorMap = { [key: string]: string }




const CustomLegend = ({ payload, colorMap, customOrder }) => {

  return (
    <ul style={{ listStyle: 'none', padding: 0 }}>
      {customOrder.slice().reverse().map((key, index) => { // Reverse the order using slice() to avoid mutating the original
        const entry = payload.find(item => item.value === key);
        if (!entry) return null; // Skip if no entry found

        return (
          <li key={`legend-item-${index}`} style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                width: 12,
                height: 12,
                backgroundColor: colorMap[key],
                borderRadius: '50%',
                marginRight: 5,
              }}
            />
            {key}
          </li>
        );
      })}
    </ul>
  );
};


const isHolliday = (date: string):string|null => {
  return holidays.find(h => new Date(h.date).valueOf() === new Date(date).valueOf())?.label ?? null
}

const isSunday = (date: string):boolean => {
  const day = new Date(date).getDay()
  return day === 0
}

const isSaturday = (date: string): boolean => {
  const day = new Date(date).getDay()
  return day === 6
}

const BarChartView = ({
  hideLegend,
  useCostomLEgend,
  items,
  formatX = (s) => s,
  dimensions,
  handleClick,
  colorMap,
  shouldShowGraphLabel,
  highlightWeekends
}: {
  hideLegend?: boolean
  useCostomLEgend?: boolean
  items: Array<IDataItem>
  formatX?: (name: string) => string
  dimensions: DimensionsReducer
  handleClick?: (e) => void
  colorMap?: IColorMap
  shouldShowGraphLabel?: boolean
  highlightWeekends?: boolean
}) => {
  const allStacks = getAllKeys(items, ["name", "Total"]);
  const isMobile = dimensions.device !== "desktop";
  const legendOrder = allStacks;


  const highlightDays = highlightWeekends ? (items
                        .filter(d => isSunday(d.name))
                        .map(d => ({
                          x1: d.name,
                          x2: d.name,
                          legend: 'Sunday',
                          color: 'red',
                        }))).concat(items
                        .filter(d => isSaturday(d.name))
                        .map(d => ({
                          x1: d.name,
                          x2: d.name,
                          legend: 'Saturday',
                          color: 'black',
                        }))).concat(items
                        .filter(d => isHolliday(d.name))
                        .map(d => ({
                          x1: d.name,
                          x2: d.name,
                          legend: isHolliday(d.name)?? 'holliday',
                          color: 'red',
                        }))
                        ) : []
                    

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        onClick={(e) => {
          if (e.activePayload && e.activePayload.length > 0) {
            const clickedData = e.activePayload[0].payload;
            handleClick ? handleClick(clickedData) : console.warn("Not implemented")
          }
        }}
        data={items}
        margin={
          isMobile
            ? { top: 20, right: 0, left: 0, bottom: 5 }
            : { top: 20, right: 30, left: 20, bottom: 5 }
        }
      >
        <CartesianGrid stroke={"#ccc"} strokeDasharray="3" />
        <XAxis dataKey="name" tickFormatter={formatX} />
        <YAxis />
        <Tooltip
          allowEscapeViewBox={{ x: false, y: true }} 
          wrapperStyle={{ zIndex: 1000 }}
          labelFormatter={(label: string) =>
            DateTime.fromISO(label).toFormat("dd MMM yyyy")
          }
          itemSorter={(item) => -(Number(item.value) || 0)}
          itemStyle={{ fontWeight: '600' }}
        />
        {!hideLegend &&
          <Legend
            layout={isMobile ? "horizontal" : "vertical"}
            verticalAlign="top"
            align={isMobile ? "left" : "right"}
            wrapperStyle={{
              paddingLeft: 20,
              paddingBottom: isMobile ? 20 : "auto",
            }}
            content={useCostomLEgend ? <CustomLegend payload={items} colorMap={colorMap} customOrder={legendOrder} /> : undefined}
          />
        }

        {highlightDays.map((d, idx) => (
          <ReferenceArea key={idx} x1={d.x1} x2={d.x2} opacity={0.1} fill={d.color} />
        ))}


        {allStacks.map((r, index) => (
          <Bar
            key={index}
            dataKey={r}
            stackId="a"
            fill={colorMap ? colorMap[r] ?? colors[index] : colors[index]}
          >
            {
              shouldShowGraphLabel
                ?
                index === allStacks.length - 1 && (
                  <LabelList
                    dataKey="Total"
                    position="top"
                    fill="black"
                    fontSize={14}
                    fontWeight="bold"
                  />
                )
                : null
            }
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const mapStateToProps = (state: StoreState) => ({
  dimensions: state.dimensions,
});

export default connect(mapStateToProps)(BarChartView);
